import React, { FC, useContext, useState } from 'react'
import { LoaderCircle, TrashIcon } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../../ui/dropdown-menu'
import { Button } from '../../../../ui/button'
import { IconCheckBook } from '../../../../icons/IconCheckBook'
import { IconCancel } from '../../../../icons/IconCancel'
import { IconCreate } from '../../../../icons/IconCreate'
import { IconTransaction } from '../../../../icons/IconTransaction'
import { IconBanknotes } from '../../../../icons/IconBanknotes'
import { IconUser } from '../../../../icons/IconUser'
import { IconMuseum } from '../../../../icons/IconMuseum'
import { IconMagneticCard } from '../../../../icons/IconMagneticCard'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../ui/command'
import { IconCheckMark } from '../../../../icons/IconCheckMark'
import customImageComponent from '../../../../common/CustomImageComponent'
import { MarkPaidByCashOptions } from '../MarkPaidByCashOptions/MarkPaidByCashOptions'
import { ACHPayment } from '../ACHPayment/ACHPayment'
import { CartPayment } from '../CartPayment/CartPayment'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip'

import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'

import { useAppSelector } from '../../../../../store/hooks'
import { selectProfileData } from '../../../../../store/user/userSlice'

import { IEventTeamMemberInterface } from '../../../../../hooks/useEventRiderTeamMembers'
import {
  IPaymentStatus,
  IPaymentType,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
} from '../../../../../types/payment'
import {
  IPaymentsCards,
  ISelectedCard,
  IUserInterface,
} from '../../../../../models/users/user.interface'
import { IRiderTeamMemberInterface } from '../../../../../models/rider-team-member/riderTeamMember.interface'
import { IEventReviewPublish } from '../../../../../models/event-review-publish/event-review-publish.interface'

import { MODAL_CONSTS } from '../../../../../const/modal-const'

interface PaymentActionsProps {
  isRefundPending: boolean
  eventTeamMembers: IEventTeamMemberInterface
  paymentStatus: IPaymentStatus
  paymentType: IPaymentType
  isScratched: boolean
  isRefunded: boolean
  paymentsCards: IPaymentsCards
  organizer: IUserInterface | null
  user: IUserInterface | null
  setPaymentsCards: (cards: IPaymentsCards) => void
  onMarkPaidAction: (values: {
    type: IPaymentType
    checkNumber?: string
    checkMemo?: string
  }) => Promise<void>
  onScratch: (isScratched: boolean) => Promise<void>
  onAdjust: () => void
  onRefund: () => void
  onSaveAssign: (selected: IRiderTeamMemberInterface) => Promise<void>
  onPayByCard: (card: ISelectedCard) => Promise<void>
  onDelete: () => Promise<void>
  registrationDocId: string
  eventReviewPublish?: IEventReviewPublish | null
}
export const PaymentActions: FC<PaymentActionsProps> = ({
  isRefundPending,
  eventTeamMembers,
  paymentStatus,
  paymentType,
  isScratched,
  isRefunded,
  paymentsCards,
  organizer,
  user,
  setPaymentsCards,
  onMarkPaidAction,
  onScratch,
  onAdjust,
  onRefund,
  onSaveAssign,
  onPayByCard,
  onDelete,
  registrationDocId,
  eventReviewPublish,
}) => {
  const handleModalContext = useContext(HandleModalContext)

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [selectedMember, setSelectedMember] = useState<IRiderTeamMemberInterface | null>(null)
  const [open, setOpen] = useState(false)

  const onChangeTeamMember = (value: IRiderTeamMemberInterface) => {
    setSelectedMember(value)
  }

  const allTeamMembers = Object.values(eventTeamMembers).flatMap((items) =>
    items.map((item) => item.teamMember)
  )

  // Remove duplicates and get team members associated with the current registration
  const teamMembers = allTeamMembers
    .filter(
      (member, index, self) =>
        index === self.findIndex((m) => m.teamMemberId === member.teamMemberId)
    )
    .filter((item) => item.registrationDocId === registrationDocId)

  const onConfirmTeamMembers = async () => {
    setLoading(true)
    if (selectedMember) await onSaveAssign(selectedMember)
    setSelectedMember(null)
    setOpen(!open)
    setLoading(false)
  }

  const allowScratches =
    eventReviewPublish?.EventDetails?.allowScratches || profileData.id === eventReviewPublish?.owner

  const allowCardPay =
    eventReviewPublish?.EventPaymentSettings?.allowCreditCardPayment ||
    eventReviewPublish?.EventPaymentSettings?.allowACHPayment ||
    organizer?.id === profileData.id
  const allowACHPay =
    eventReviewPublish?.EventPaymentSettings?.allowACHPayment || organizer?.id === profileData.id

  return (
    <DropdownMenu dir="rtl" open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="link" className="group rounded-full p-0 w-[18px] h-auto" size="icon">
          {loading || deleteLoading ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <IconCreate className="group-hover:fill-SeabiscuitMainThemeColor" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[260px]" align="start">
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={paymentStatus === PAYMENT_STATUS.PAID || isScratched || !allowCardPay}
              className="dropdown-menu-sub-trigger"
            >
              <IconMagneticCard className="w-[12px]" />
              Charge credit card
            </DropdownMenuSubTrigger>
            <CartPayment
              paymentsCards={paymentsCards}
              setPaymentsCards={setPaymentsCards}
              setLoading={setLoading}
              onPayByCardAction={onPayByCard}
              organizer={organizer || null}
              user={user}
            />
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={paymentStatus === PAYMENT_STATUS.PAID || isScratched || !allowACHPay}
              className="dropdown-menu-sub-trigger"
            >
              <IconMuseum className="w-[12px]" />
              Make ACH payment
            </DropdownMenuSubTrigger>
            <ACHPayment
              paymentsCards={paymentsCards}
              setPaymentsCards={setPaymentsCards}
              user={user}
              organizer={organizer || null}
              setLoading={setLoading}
              onPayByCardAction={onPayByCard}
            />
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={paymentStatus === PAYMENT_STATUS.PAID || isScratched}
              className="dropdown-menu-sub-trigger"
            >
              <IconUser className="w-[12px]" />
              Assign to someone else
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-[260px] direction-ltr">
                <Command>
                  <CommandInput
                    placeholder="Search users"
                    autoFocus={true}
                    className="h-7 mb-1 !border-transparent outline-none shadow-none bg-[#F8F8F8]"
                  />
                  <CommandList>
                    <DropdownMenuLabel>Team members</DropdownMenuLabel>
                    <CommandEmpty>No members found</CommandEmpty>
                    <CommandGroup>
                      {teamMembers.map(
                        (item, index) =>
                          item.id && (
                            <CommandItem
                              key={index}
                              value={item.id}
                              onSelect={() => onChangeTeamMember(item)}
                            >
                              {customImageComponent(
                                item?.teamMemberProfilePicture,
                                item.teamMemberName,
                                'w-6 h-6 min-w-6 rounded-full'
                              )}
                              {item.teamMemberName}
                              {selectedMember?.teamMemberId === item.teamMemberId && (
                                <IconCheckMark className="ml-auto w-[12px]" />
                              )}
                            </CommandItem>
                          )
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
                <div className="p-2">
                  <Button
                    disabled={!selectedMember || loading}
                    variant="red"
                    className="w-full h-8"
                    size="sm"
                    onClick={onConfirmTeamMembers}
                  >
                    {loading && <LoaderCircle className="animate-spin !w-3" />}
                    Confirm
                  </Button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {organizer?.id === profileData.id && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                disabled={paymentStatus === PAYMENT_STATUS.PAID || isScratched}
                className="dropdown-menu-sub-trigger"
              >
                <IconBanknotes className="w-[12px] fill-black" />
                Mark paid by cash
              </DropdownMenuSubTrigger>
              <MarkPaidByCashOptions setLoading={setLoading} onMarkPaidAction={onMarkPaidAction} />
            </DropdownMenuSub>
          )}
          {organizer?.id === profileData.id && (
            <DropdownMenuItem
              disabled={paymentStatus === PAYMENT_STATUS.PAID || isScratched}
              onClick={async () => {
                handleModalContext?.handleModal(true, MODAL_CONSTS.CHECK_DETAILS, {
                  ...handleModalContext?.dataToPassOn,
                  onSave: async (values: { checkNumber: string; checkMemo: string }) =>
                    await onMarkPaidAction({
                      type: PAYMENT_TYPE.CHECK,
                      checkNumber: values.checkNumber,
                      checkMemo: values.checkMemo,
                    }),
                })
              }}
            >
              <IconCheckBook className="w-[12px] fill-black" />
              Mark paid by check
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Tooltip>
            <TooltipTrigger className="w-full" asChild>
              {isScratched ? (
                <DropdownMenuItem
                  className="w-full"
                  disabled={isRefunded || !allowScratches}
                  onClick={async () => {
                    setLoading(true)
                    await onScratch(true)
                    setLoading(false)
                  }}
                >
                  <IconCancel className="w-[12px]" />
                  Unscratch
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="w-full"
                  disabled={isScratched || !allowScratches}
                  onClick={async () => {
                    setLoading(true)
                    await onScratch(false)
                    setLoading(false)
                  }}
                >
                  <IconCancel className="w-[12px]" />
                  Scratch
                </DropdownMenuItem>
              )}
            </TooltipTrigger>
            <TooltipContent hidden={allowScratches}>Disabled by show</TooltipContent>
          </Tooltip>
          {organizer?.id === profileData.id && (
            <DropdownMenuItem
              disabled={paymentStatus === PAYMENT_STATUS.PAID || isRefunded || isScratched}
              onClick={onAdjust}
            >
              <IconCreate className="w-[12px] hover:fill-SeabiscuitMainThemeColor" />
              Adjust
            </DropdownMenuItem>
          )}
          {organizer?.id === profileData.id && (
            <DropdownMenuItem disabled={!isRefundPending} onClick={onRefund}>
              <IconTransaction className="w-[12px]" />
              Refund
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={deleteLoading || paymentType === PAYMENT_TYPE.CARD}
            onClick={async () => {
              setDeleteLoading(true)
              await onDelete()
              setDeleteLoading(false)
            }}
          >
            <TrashIcon className="w-[12px]" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
